import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Button, BackIcon, ArrowRightIcon, BookOpenIcon, DocumentIcon, Badge } from '../design-system';
import { useCreateIdea } from '../hooks/api/useIdeas';
import { useApp } from '../contexts/AppContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import RepositorySelector from '../components/RepositorySelector';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: T.paperSoft,
  border: `1px solid ${T.rule}`,
  borderRadius: T.r3,
  color: T.ink,
  fontSize: '15px',
  lineHeight: 1.5,
  fontFamily: T.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: 600,
  fontSize: '13px',
  color: T.ink2,
  fontFamily: T.sans,
};

// ---------------------------------------------------------------------------
// Flow selection screen
// ---------------------------------------------------------------------------

const FlowCard = ({ icon, badge, title, description, actionLabel, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: T.card,
        border: `1px solid ${hovered && !disabled ? T.ruleStrong : T.rule}`,
        borderRadius: T.r4,
        padding: '24px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        boxShadow: hovered && !disabled ? T.sh2 : T.sh1,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{
          width: 44,
          height: 44,
          borderRadius: T.r3,
          backgroundColor: T.paperDeep,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {icon}
        </div>
        {badge}
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: '16px', color: T.ink, marginBottom: '8px', fontFamily: T.sans }}>
          {title}
        </div>
        <div style={{ fontSize: '14px', color: T.inkMuted, lineHeight: 1.6, fontFamily: T.sans }}>
          {description}
        </div>
      </div>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: 600,
        color: T.orange,
        fontFamily: T.sans,
      }}>
        {actionLabel} <ArrowRightIcon size={14} style={{ color: T.orange }} />
      </div>
    </div>
  );
};

const FlowSelection = ({ onSelect, canUseGitHub, onUpgrade, isMobile }) => (
  <div style={{
    paddingTop: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    fontFamily: T.sans,
  }}>
    <header style={{ marginBottom: '40px', maxWidth: '520px' }}>
      <div style={{ fontFamily: T.hand, fontSize: '32px', color: T.orange, marginBottom: '8px' }}>
        what are you working on?
      </div>
      <div style={{ fontSize: '16px', color: T.inkMuted }}>
        Choose how you'd like to get started with Cahoots.
      </div>
    </header>
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '16px',
      width: '100%',
      maxWidth: '680px',
      textAlign: 'left',
    }}>
      <FlowCard
        icon={<DocumentIcon size={24} style={{ color: T.inkMuted }} />}
        title="Starting something new?"
        description="Describe your idea and we'll build a spec, break it into stories, and keep everything organised."
        actionLabel="Get started"
        onClick={() => onSelect('new')}
      />
      <FlowCard
        icon={<BookOpenIcon size={24} style={{ color: canUseGitHub ? T.inkMuted : T.inkFaint }} />}
        badge={!canUseGitHub && <Badge variant="warning" size="sm">Pro</Badge>}
        title="Document what you've built?"
        description={
          canUseGitHub
            ? "Connect your repo and we'll generate living docs that stay in sync with your codebase."
            : "Connect your GitHub repo to generate living documentation. Available on the Pro plan."
        }
        actionLabel={canUseGitHub ? 'Connect repo' : 'Upgrade to Pro'}
        onClick={canUseGitHub ? () => onSelect('existing') : onUpgrade}
        disabled={!canUseGitHub}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const IdeaCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useApp();
  const { canUseGitHub } = useSubscription();
  const posthog = usePostHog();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasGitHub = canUseGitHub();
  const initialFlow = searchParams.get('flow') === 'existing' && hasGitHub ? 'existing' : null;
  const [flowType, setFlowType] = useState(initialFlow);

  const [ideaName, setIdeaName] = useState('');
  const [ideaDescription, setIdeaDescription] = useState('');
  const [selectedRepo, setSelectedRepo] = useState(null);

  const createIdeaMutation = useCreateIdea();

  const buildGithubRepos = () => {
    if (!selectedRepo?.url) return undefined;
    const match = selectedRepo.url.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (!match) return undefined;
    return [{
      url: `https://github.com/${match[1]}/${match[2]}`,
      owner: match[1],
      repo: match[2],
      default_branch: selectedRepo.branch || 'main',
    }];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isExisting = flowType === 'existing';

    if (isExisting && !selectedRepo) {
      showError('Please connect a GitHub repository to continue');
      return;
    }
    if (!isExisting && !ideaDescription.trim()) {
      showError('Please describe your idea');
      return;
    }

    try {
      const result = await createIdeaMutation.mutateAsync({
        idea: ideaDescription.trim() || undefined,
        name: ideaName.trim() || undefined,
        github_repos: buildGithubRepos(),
      });

      posthog?.capture('idea_created', {
        flow_type: isExisting ? 'existing' : 'new',
        has_name: !!ideaName.trim(),
        has_github_repo: !!buildGithubRepos(),
      });

      showSuccess(isExisting
        ? 'Connected! Analyzing your repository...'
        : "Idea created! Let's start the discovery process."
      );

      if (result?.id) {
        navigate(`/ideas/${result.id}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      showError(error.message || 'Failed to create idea');
    }
  };

  // ---------------------------------------------------------------------------
  // Flow selection screen
  // ---------------------------------------------------------------------------
  if (!flowType) {
    return (
      <div className="container fade-in" style={{ fontFamily: T.sans }}>
        <div style={{ paddingTop: '16px' }}>
          <Button variant="ghost" size="sm" icon={BackIcon} onClick={() => navigate('/dashboard')}>
            Dashboard
          </Button>
        </div>
        <FlowSelection
          onSelect={setFlowType}
          canUseGitHub={hasGitHub}
          onUpgrade={() => navigate('/pricing')}
          isMobile={isMobile}
        />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Form
  // ---------------------------------------------------------------------------
  const isExistingFlow = flowType === 'existing';
  const isSubmitDisabled = createIdeaMutation.isPending || (
    isExistingFlow ? !selectedRepo : !ideaDescription.trim()
  );

  return (
    <div className="container fade-in" style={{ paddingTop: '24px', fontFamily: T.sans }}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ marginBottom: '12px' }}>
            <Button variant="ghost" size="sm" icon={BackIcon} onClick={() => setFlowType(null)}>
              Back
            </Button>
          </div>
          <div style={{ fontFamily: T.hand, fontSize: '32px', color: T.orange, lineHeight: 1.1, marginBottom: '6px' }}>
            {isExistingFlow ? 'document your product' : 'new idea'}
          </div>
          <div style={{ fontSize: '15px', color: T.inkMuted, lineHeight: 1.5 }}>
            {isExistingFlow
              ? "Connect your GitHub repository and we'll generate living documentation from your codebase."
              : 'Describe your idea and let AI help you plan it out.'
            }
          </div>
        </div>

        {/* Form card */}
        <div style={{
          backgroundColor: T.card,
          border: `1px solid ${T.rule}`,
          borderRadius: T.r4,
          padding: '28px',
          boxShadow: T.sh2,
        }}>
          <form onSubmit={handleSubmit}>

            {/* GitHub Repository */}
            {isExistingFlow && (
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>
                  GitHub Repository <span style={{ color: T.inkFaint, fontWeight: 400 }}>(required)</span>
                </label>
                <RepositorySelector value={selectedRepo} onChange={setSelectedRepo} />
                <div style={{ marginTop: '6px', fontSize: '13px', color: T.inkMuted }}>
                  We'll analyze the codebase to generate a spec that reflects what's actually built.
                </div>
              </div>
            )}

            {/* Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Name <span style={{ color: T.inkFaint, fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                value={ideaName}
                onChange={(e) => setIdeaName(e.target.value)}
                placeholder={isExistingFlow
                  ? 'e.g., Cahoots API, Payment Service, Admin Dashboard'
                  : 'e.g., TaskFlow, BookShelf, WeatherPro'
                }
                maxLength={100}
                style={inputStyle}
              />
              <div style={{ marginTop: '4px', fontSize: '12px', color: T.inkFaint }}>
                {isExistingFlow
                  ? "We'll infer a name from the repo if you leave this empty."
                  : "We'll generate one if you leave this empty."
                }
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>
                {isExistingFlow ? 'Additional Context' : 'Describe Your Idea'}
                {isExistingFlow && <span style={{ color: T.inkFaint, fontWeight: 400 }}> (optional)</span>}
              </label>
              <textarea
                value={ideaDescription}
                onChange={(e) => setIdeaDescription(e.target.value)}
                placeholder={isExistingFlow
                  ? "Anything the repo doesn't make obvious — planned features, migration goals, team context."
                  : 'Describe what you want to build. What problem does it solve? Who is it for? What are the key features you envision?\n\nExample: I want to build a mobile app that helps busy professionals track their daily water intake...'
                }
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: isExistingFlow ? '120px' : '200px',
                  lineHeight: 1.6,
                }}
                autoFocus={!isExistingFlow}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                <div style={{ fontSize: '12px', color: T.inkFaint }}>
                  {isExistingFlow
                    ? 'Leave blank to infer from the repository.'
                    : 'Be as detailed as you like. More context = better output.'}
                </div>
                <div style={{ fontSize: '12px', color: T.inkFaint }}>{ideaDescription.length} chars</div>
              </div>
            </div>

            {/* What happens next */}
            {!isExistingFlow && (
              <div style={{
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: T.paperSoft,
                border: `1px dashed ${T.ruleStrong}`,
                borderRadius: T.r3,
              }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: T.inkMuted, marginBottom: '10px' }}>
                  What happens next
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    ['Review', 'AI summarizes your idea for confirmation'],
                    ['Assumptions & Risks', 'Surface what we\u2019re assuming and flag considerations'],
                    ['Scope & Plan', 'Choose what goes in MVP vs later phases'],
                  ].map(([label, detail], i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', color: T.ink2 }}>
                      <span style={{ color: T.orange, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                      <span><strong>{label}:</strong> {detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button type="button" variant="secondary" onClick={() => setFlowType(null)} disabled={createIdeaMutation.isPending}>
                Back
              </Button>
              <Button type="submit" variant="primary" loading={createIdeaMutation.isPending} disabled={isSubmitDisabled}>
                {isExistingFlow ? 'Generate Documentation' : 'Create Idea'}
              </Button>
            </div>
          </form>
        </div>

        {/* Tips — only for new flow */}
        {!isExistingFlow && (
          <div style={{ marginTop: '32px', marginBottom: '40px' }}>
            <div style={{ fontWeight: 700, fontSize: '15px', color: T.ink, marginBottom: '14px' }}>
              Tips for a great idea
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '12px',
            }}>
              {[
                ['Be specific about the problem', '\u201cA todo app for teams that need to track dependencies\u201d beats \u201ca todo app\u201d'],
                ['Mention your target users', 'Who will use this? Developers? Small business owners? Students? This helps prioritize features.'],
                ['Include key features', "List 3\u20135 features you consider essential. Don\u2019t worry about being exhaustive."],
                ['Mention constraints', 'Technical requirements, integrations, or limitations that matter for your use case.'],
              ].map(([title, body], i) => (
                <div key={i} style={{
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r3,
                  padding: '16px',
                }}>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: T.ink, marginBottom: '6px' }}>{title}</div>
                  <div style={{ fontSize: '13px', color: T.inkMuted, lineHeight: 1.5 }}>{body}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeaCreate;
